import {
  AddButtonProps,
  CommonLabelProps,
  FormikCheckboxProps,
  FormikComboboxProps,
  FormikDateFieldProps,
  FormikTextFieldProps,
  SelectOption
} from '../..'

export interface RowOptions<RowDataType> {
  key: keyof RowDataType
  options: SelectOption[]
}

type RowStyleProps = {
  addAlertClass?: boolean
}

type EditableRowProps<RowDataType> = {
  rowOptions?: RowOptions<RowDataType>[]
  isRowEditable?: boolean
  isRowNew?: boolean
}

export type DenseTableEditableAndStyleRowProps<RowDataType> =
  EditableRowProps<RowDataType> & RowStyleProps

export type DenseTableRowProps<RowDataType> = RowDataType &
  DenseTableEditableAndStyleRowProps<RowDataType>

export enum CellInputType {
  Text = 'text',
  Date = 'date',
  CheckBox = 'checkbox',
  ComboBox = 'combobox'
}

interface CellInputFieldType<RowDataType> {
  inputKey?: keyof RowDataType
  isEditingDisabledForNewItem?: boolean
  isEditingDisabledForOldItem?: boolean
  showOnlyForNewItem?: boolean
}

interface CellInputTextFieldType<RowDataType>
  extends CellInputFieldType<RowDataType> {
  type: CellInputType.Text
  inputProps?: Pick<
    FormikTextFieldProps,
    'label' | 'labelTranslationId' | 'placeholder' | 'placeholderTranslationId'
  >
}

interface CellInputDateFieldType<RowDataType>
  extends CellInputFieldType<RowDataType> {
  type: CellInputType.Date
  inputProps?: Pick<
    FormikDateFieldProps,
    'label' | 'labelTranslationId' | 'placeholder' | 'placeholderTranslationId'
  >
}

interface CellInputCheckboxType<RowDataType>
  extends CellInputFieldType<RowDataType> {
  type: CellInputType.CheckBox
  inputProps?: Pick<FormikCheckboxProps, 'label' | 'labelTranslationId'>
}

export interface CellInputComboboxType<RowDataType>
  extends CellInputFieldType<RowDataType> {
  type: CellInputType.ComboBox
  showTextFieldIfNoData?: boolean
  inputProps?: Pick<
    FormikComboboxProps,
    | 'hidefield'
    | 'label'
    | 'labelTranslationId'
    | 'placeholder'
    | 'placeholderTranslationId'
  >
}

type CellInputProps<RowDataType> =
  | CellInputTextFieldType<RowDataType>
  | CellInputDateFieldType<RowDataType>
  | CellInputCheckboxType<RowDataType>
  | CellInputComboboxType<RowDataType>

export interface CellProps<RowDataType> extends CommonLabelProps {
  key: keyof RowDataType
  keyToCopy?: keyof RowDataType
  columnWidth?: string
  dateFormat?: string
  displayWhenNull?: string
  hideColumn?: boolean
  hideIfNoValues?: boolean
  inputTypeProps?: CellInputProps<RowDataType>
  money?: boolean
}

interface AddRowButtonProps<RowDataType>
  extends Omit<AddButtonProps, 'marginSize' | 'handleClick'> {
  newRowValue: DenseTableRowProps<RowDataType>
}

interface FormProps<RowDataType> {
  addButtonProps?: AddRowButtonProps<RowDataType>
}

export enum TableType {
  Dynamic = 'dynamic',
  Static = 'static'
}

export interface TableStylingProps {
  addBorderToFirstMobileColumnBox?: boolean
}

export interface BasicTableProps<RowDataType> {
  type: TableType
  cellProps: CellProps<RowDataType>[]
  className?: string
  tableStylingProps?: TableStylingProps
}

interface TableDataType<RowDataType> {
  data: DenseTableRowProps<RowDataType>[]
}

export interface StaticTableProps<RowDataType>
  extends BasicTableProps<RowDataType>,
    TableDataType<RowDataType> {
  type: TableType.Static
}

export interface FormTableProps<RowDataType>
  extends BasicTableProps<RowDataType> {
  type: TableType.Dynamic
  formProps: FormProps<RowDataType>
  tableName: string
}

export type DenseTableProps<RowDataType> =
  | StaticTableProps<RowDataType>
  | FormTableProps<RowDataType>

export type TableContentProps<RowDataType> =
  | StaticTableProps<RowDataType>
  | (Omit<FormTableProps<RowDataType>, 'formProps'> &
      TableDataType<RowDataType>)

type StaticTableType<RowDataType> = Pick<StaticTableProps<RowDataType>, 'type'>

type DynamicTableType<RowDataType> = Pick<
  FormTableProps<RowDataType>,
  'type' | 'tableName'
>

interface BasicContentProps<RowDataType>
  extends TableDataType<RowDataType>,
    Pick<BasicTableProps<RowDataType>, 'tableStylingProps'> {
  filtered: CellProps<RowDataType>[]
}

export type StaticContentProps<RowDataType> = BasicContentProps<RowDataType> &
  StaticTableType<RowDataType>

export type DynamicContentProps<RowDataType> = BasicContentProps<RowDataType> &
  DynamicTableType<RowDataType>

export type ContentProps<RowDataType> =
  | StaticContentProps<RowDataType>
  | DynamicContentProps<RowDataType>

interface CellContentType<RowDataType> {
  row: DenseTableRowProps<RowDataType>
  cellProps: CellProps<RowDataType>
  isMobile: boolean
}

export type TextCellContentProps<RowDataType> = CellContentType<RowDataType> &
  StaticTableType<RowDataType>

export interface InputCellContentProps<RowDataType>
  extends CellContentType<RowDataType>,
    DynamicTableType<RowDataType> {
  index: number
}

export type CellContentProps<RowDataType> =
  | TextCellContentProps<RowDataType>
  | InputCellContentProps<RowDataType>
